import React from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation, Outlet } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/toaster';
import AuthCallback from './components/AuthCallback';

// dutyai.com Layout Components (marketing/public site)
import Navbar from './components/Navbar';
import HeroSection from './components/HeroSection';
import FeaturesSection from './components/FeaturesSection';
import StatsSection from './components/StatsSection';
import AccelerateSection from './components/AccelerateSection';
import TestimonialsSection from './components/TestimonialsSection';
import CandidatesSection from './components/CandidatesSection';
import CTASection from './components/CTASection';
import Footer from './components/Footer';

// Duty AI Layout Components (app/dashboard)
import AdminLayout from './components/admin/AdminLayout';
import { SidebarProvider as DashSidebarProvider } from './dashboard/context/SidebarContext';
import DashAnalytics from './dashboard/pages/Analytics';
import DashAIInterviewer from './dashboard/pages/AIInterviewer';
import DashSettings from './dashboard/pages/Settings';
import DashOpenJobs from './dashboard/pages/OpenJobs';

// dutyai.com Static Pages
import DemoPage from './pages/DemoPage';
import ProductPage from './pages/ProductPage';
import CustomersPage from './pages/CustomersPage';
import CandidatesPage from './pages/CandidatesPage';
import CustomerStoryPage from './pages/CustomerStoryPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsPage from './pages/TermsPage';

// Duty AI Homepage (for /jobs route)
import MercorHomePage from './pages/MercorHomePage';

// Duty AI Public Pages
import JobsPage from './pages/JobsPage';
import JobDetailPage from './pages/JobDetailPage';
import MercorJobDetailPage from './pages/MercorJobDetailPage';
import AboutPage from './pages/AboutPage';
import BlogPage from './pages/BlogPage';
import BlogPostPage from './pages/BlogPostPage';
import TypeformRedirect from './pages/TypeformRedirect';
import LoginPage from './pages/LoginPage';
import StoriesPage from './pages/StoriesPage';
import CareersPage from './pages/CareersPage';
import HumanDataPage from './pages/HumanDataPage';
import ProcessPage from './pages/ProcessPage';
import CandidateFAQPage from './pages/CandidateFAQPage';
import NewsroomPage from './pages/NewsroomPage';
import EthicalAIPage from './pages/EthicalAIPage';
import GetInTouchPage from './pages/GetInTouchPage';
import AIAgentsPage from './pages/AIAgentsPage';
import EnterprisePage from './pages/EnterprisePage';

// Duty AI Sidebar/Dashboard Pages
import ExplorePage from './pages/ExplorePage';
import ProfilePage from './pages/ProfilePage';
import MercorProfilePage from './pages/MercorProfilePage';
import EarningsPage from './pages/EarningsPage';
import MercorEarningsPage from './pages/MercorEarningsPage';
import HomeDashboard from './pages/HomeDashboard';
import ReferralsPage from './pages/ReferralsPage';
import MercorReferralsPage from './pages/MercorReferralsPage';
import InterviewPage from './pages/InterviewPage';
import InterviewResumePage from './pages/InterviewResumePage';
import InterviewLivePage from './pages/InterviewLivePage';
import AIToolsPage from './pages/AIToolsPage';

// Duty AI Feature Pages
import NotificationsPage from './pages/NotificationsPage';
import MessagesPage from './pages/MessagesPage';
import SettingsPage from './pages/SettingsPage';
import ContractsPage from './pages/ContractsPage';
import TimeTrackingPage from './pages/TimeTrackingPage';
import BackgroundCheckPage from './pages/BackgroundCheckPage';
import EndorsementsPage from './pages/EndorsementsPage';
import WeeklySurveysPage from './pages/WeeklySurveysPage';
import WorkTrialsPage from './pages/WorkTrialsPage';
import InstantOffersPage from './pages/InstantOffersPage';
import TaxesPage from './pages/TaxesPage';
import OnboardingPage from './pages/OnboardingPage';
import ApplicationFlowPage from './pages/ApplicationFlowPage';

// Candidate Pages
import CandidateProfilePage from './pages/CandidateProfilePage';
import CandidateApplicationsPage from './pages/CandidateApplicationsPage';
import ApplyJobPage from './pages/ApplyJobPage';

// Company Pages
import CompanyProfilePage from './pages/CompanyProfilePage';
import CompanyJobsPage from './pages/CompanyJobsPage';
import CompanyApplicationsPage from './pages/CompanyApplicationsPage';
import PostJobPage from './pages/PostJobPage';

// Admin Pages
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminJobs from './pages/admin/AdminJobs';
import AdminUsers from './pages/admin/AdminUsers';
import AdminApplications from './pages/admin/AdminApplications';
import AdminBlog from './pages/admin/AdminBlog';

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return <div className="flex items-center justify-center min-h-screen bg-[#F9FBF5] text-[#082900]">Loading...</div>;
  }
  if (!user) return <Navigate to="/login" replace />;
  if (allowedRoles && !allowedRoles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
};

// dutyai.com Homepage Component (marketing landing page)
const AlexHomePage = () => (
  <>
    <Navbar />
    <HeroSection />
    <FeaturesSection />
    <StatsSection />
    <AccelerateSection />
    <TestimonialsSection />
    <CandidatesSection />
    <CTASection />
    <Footer />
  </>
);

// Layout for dutyai.com public/marketing pages (Navbar + Footer)
const AlexPublicLayout = ({ children }) => (
  <>
    <Navbar />
    {children}
    <Footer />
  </>
);

// Layout for Duty AI public pages (uses Duty AI's own header/footer from within pages)
const MercorPublicLayout = ({ children }) => (
  <>
    <Navbar />
    {children}
    <Footer />
  </>
);

// AppRouter handles auth callback detection
function AppRouter() {
  const location = useLocation();

  // Check URL fragment for session_id (Emergent Auth)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Auth Callback Route */}
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/login" element={<LoginPage />} />

      {/* === dutyai.com MARKETING PAGES (these have internal Navbar+Footer) === */}
      <Route path="/" element={<AlexHomePage />} />
      <Route path="/demo" element={<DemoPage />} />
      <Route path="/product/:slug" element={<ProductPage />} />
      <Route path="/customers" element={<CustomersPage />} />
      <Route path="/candidates-info" element={<CandidatesPage />} />
      <Route path="/customer-stories/:slug" element={<CustomerStoryPage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/terms-of-service" element={<TermsPage />} />
      <Route path="/candidate-faq" element={<CandidateFAQPage />} />
      <Route path="/newsroom" element={<NewsroomPage />} />
      <Route path="/ethical-ai" element={<EthicalAIPage />} />
      <Route path="/ai-agents" element={<AIAgentsPage />} />
      <Route path="/enterprise" element={<EnterprisePage />} />

      {/* === DUTY AI JOB PLATFORM PAGES (Public) === */}
      <Route path="/jobs" element={<><Navbar /><MercorHomePage /><Footer /></>} />
      <Route path="/jobs/:id" element={<AlexPublicLayout><JobDetailPage /></AlexPublicLayout>} />
      <Route path="/jobs/:id/:slug" element={<MercorJobDetailPage />} />
      <Route path="/about" element={<AlexPublicLayout><AboutPage /></AlexPublicLayout>} />
      <Route path="/blog" element={<GetInTouchPage />} />
      <Route path="/get-in-touch" element={<GetInTouchPage />} />
      <Route path="/contact" element={<GetInTouchPage />} />
      <Route path="/blog/:slug" element={<AlexPublicLayout><BlogPostPage /></AlexPublicLayout>} />
      <Route path="/contact" element={<TypeformRedirect />} />
      <Route path="/stories" element={<StoriesPage />} />
      <Route path="/careers" element={<CareersPage />} />
      <Route path="/for-employer" element={<CareersPage />} />
      <Route path="/human-data" element={<HumanDataPage />} />
      <Route path="/process" element={<ProcessPage />} />

      {/* === DUTY AI DASHBOARD PAGES (Sidebar layout, no Header/Footer) === */}
      <Route path="/explore" element={<ExplorePage />} />
      <Route path="/home" element={<HomeDashboard />} />
      <Route path="/profile" element={<MercorProfilePage />} />
      <Route path="/earnings" element={<MercorEarningsPage />} />
      <Route path="/referrals" element={<MercorReferralsPage />} />
      {/* Default candidate landing. Frappe writes this URL into
          `duty_ai_candidate_url` on every Interview record (see
          duty_ai_connector). Points at the live Apriora-style voice UI:
          camera preview + pre-flight device check + LiveKit room with
          AI agent + S3 recording. */}
      <Route path="/interview/by-id/:interviewId" element={<InterviewLivePage />} />
      {/* Alias kept for backward compatibility with URLs already shared
          by HR. Same component, identical behaviour. */}
      <Route path="/interview/live/by-id/:interviewId" element={<InterviewLivePage />} />
      {/* Text-only fallback for candidates with broken camera/mic. Hits
          the same backend Q&A endpoints, no LiveKit. */}
      <Route path="/interview/text/by-id/:interviewId" element={<InterviewResumePage />} />
      <Route path="/interview/:applicationId" element={<InterviewPage />} />
      <Route path="/ai-tools" element={<AIToolsPage />} />

      {/* === DUTY AI FEATURE PAGES === */}
      <Route path="/notifications" element={<NotificationsPage />} />
      <Route path="/messages" element={<MessagesPage />} />
      <Route path="/settings" element={<SettingsPage />} />
      <Route path="/contracts" element={<ContractsPage />} />
      <Route path="/time-tracking" element={<TimeTrackingPage />} />
      <Route path="/background-check" element={<BackgroundCheckPage />} />
      <Route path="/endorsements" element={<EndorsementsPage />} />
      <Route path="/surveys" element={<WeeklySurveysPage />} />
      <Route path="/work-trials" element={<WorkTrialsPage />} />
      <Route path="/instant-offers" element={<InstantOffersPage />} />
      <Route path="/taxes" element={<TaxesPage />} />
      <Route path="/onboarding" element={<OnboardingPage />} />
      <Route path="/apply/:jobId" element={<ApplicationFlowPage />} />

      {/* === CANDIDATE ROUTES === */}
      <Route path="/candidate/profile" element={<ProtectedRoute allowedRoles={['candidate']}><MercorPublicLayout><CandidateProfilePage /></MercorPublicLayout></ProtectedRoute>} />
      <Route path="/candidate/applications" element={<ProtectedRoute allowedRoles={['candidate']}><MercorPublicLayout><CandidateApplicationsPage /></MercorPublicLayout></ProtectedRoute>} />
      <Route path="/candidate/apply/:jobId" element={<ProtectedRoute allowedRoles={['candidate']}><MercorPublicLayout><ApplyJobPage /></MercorPublicLayout></ProtectedRoute>} />

      {/* === COMPANY ROUTES === */}
      <Route path="/company/profile" element={<ProtectedRoute allowedRoles={['company']}><MercorPublicLayout><CompanyProfilePage /></MercorPublicLayout></ProtectedRoute>} />
      <Route path="/company/jobs" element={<ProtectedRoute allowedRoles={['company']}><MercorPublicLayout><CompanyJobsPage /></MercorPublicLayout></ProtectedRoute>} />
      <Route path="/company/applications" element={<ProtectedRoute allowedRoles={['company']}><MercorPublicLayout><CompanyApplicationsPage /></MercorPublicLayout></ProtectedRoute>} />
      <Route path="/company/post-job" element={<ProtectedRoute allowedRoles={['company']}><MercorPublicLayout><PostJobPage /></MercorPublicLayout></ProtectedRoute>} />

      {/* === ADMIN ROUTES === */}
      <Route path="/admin/*" element={<ProtectedRoute allowedRoles={['admin']}><AdminLayout /></ProtectedRoute>}>
        <Route path="dashboard" element={<AdminDashboard />} />
        <Route path="jobs" element={<AdminJobs />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="applications" element={<AdminApplications />} />
        <Route path="blog" element={<AdminBlog />} />
        <Route path="testimonials" element={<AdminDashboard />} />
      </Route>

      {/* 404 */}
      {/* Recruiter/HR dashboard (roadmap #19 — ported micro1/zara UI) */}
      <Route path="/dashboard" element={<ProtectedRoute allowedRoles={['company', 'admin']}><DashSidebarProvider><Outlet /></DashSidebarProvider></ProtectedRoute>}>
        <Route index element={<Navigate to="/dashboard/open-jobs" replace />} />
        <Route path="open-jobs" element={<DashOpenJobs />} />
        <Route path="ai-interviewer" element={<DashAIInterviewer />} />
        <Route path="analytics" element={<DashAnalytics />} />
        <Route path="settings" element={<DashSettings />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <div className="App">
        <BrowserRouter>
          <AppRouter />
          <Toaster />
        </BrowserRouter>
      </div>
    </AuthProvider>
  );
}

export default App;
